import {
  media,
  modularScale,
  responsive,
  VerticalSpacing,
  breakpoints,
  ResponsiveSizes,
} from "@ottatech/design";
import styled from "styled-components";
import React, { ReactElement } from "react";
import { useMediaQuery } from "react-responsive";

import * as Expander from "components/Expander";
import { ThemeId } from "schema";
import { partition } from "utils/collections";
import { BatchCard } from "components/BatchCard";
import { Sector, Theme } from "components/BatchCard/theme";

const ThemeBatchOrder: (ThemeId | "ALL_MATCHES")[] = [
  ThemeId.ApplyViaOtta,
  ThemeId.NewlyAdded,
  ThemeId.FullyRemote,
  ThemeId.FavouriteTechnologies,
  ThemeId.RecentlyFunded,
  ThemeId.PreferredSector,
  ThemeId.HasSalaries,
  ThemeId.FemaleFounders,
  ThemeId.TechForGood,
  ThemeId.TakeAnotherLook,
];

/**
 * We can't use row-gap in the CSS grid because we hide some rows dynamically
 * so we have to be old school and give everything a nice 0.5rem margin-bottom
 */
const CardGrid = styled.div`
  z-index: 1;
  display: grid;
  column-gap: 0.5rem;

  & > * {
    margin-bottom: 0.5rem;
  }

  position: relative;
  grid-template-rows: auto;
  grid-template-columns: 1fr;
  grid-template-areas: "heading" "firstRow" "secondRow" "expander" "buttons";

  ${media({ from: "tablet" })`
    margin-top: 3rem;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      "heading buttons"
      "firstRow firstRow"
      "secondRow secondRow"
      "expander expander";
  `}
`;

/**
 * Cards are shown in sub-grids so all the cards in a row
 * can be expanded or hidden as one big item
 */
const CardRow = styled.div`
  display: grid;
  grid-gap: 0.5rem;
  ${media({ from: "tablet" })`
    grid-template-columns: repeat(4, 1fr);
    grid-template-areas: "a b c d";
  `}
  ${media({ to: "tablet" })`
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas: "a b" "c d";
  `}
`;

const FirstRow = styled.div`
  grid-area: firstRow;
`;

const SecondRow = styled(Expander.Content)`
  grid-area: secondRow;
`;

const Recommended = styled.h3`
  ${responsive`font-size: ${modularScale({ mobile: 0, desktop: 2 })};`}
  grid-area: heading;
  font-weight: 700;
`;

export type BatchCounts = Record<ThemeId, number> & {
  ALL_MATCHES: number;
};

export function toList(bc: BatchCounts): [keyof BatchCounts, number][] {
  return Object.entries(bc) as [keyof BatchCounts, number][];
}

export type BatchCardData = {
  counts: BatchCounts;
  sector: { id: string; value: string };
};

/**
 * Decide how to sort batch cards - we prioritise all matches
 * and then following the experiment 20221124_THEME_BATCH_NAVIGATION_EXPERIMENT
 * run in a priorty order as listed in PR 2757
 */

function sortOrder(
  [themeA, countA]: [keyof BatchCounts, number],
  [themeB, countB]: [keyof BatchCounts, number]
): number {
  return themeA == "ALL_MATCHES"
    ? -1
    : themeB == "ALL_MATCHES"
    ? 1
    : countA == 0
    ? 1
    : countB == 0
    ? -1
    : 0;
}

function cards(
  sector: BatchCardData["sector"],
  counts: [keyof BatchCounts, number][]
): ReactElement[] {
  counts
    .sort(
      ([themeA], [themeB]) =>
        ThemeBatchOrder.indexOf(themeA) - ThemeBatchOrder.indexOf(themeB)
    )
    .sort((a, b) => sortOrder(a, b));
  return counts.map(([key, value], index) => {
    const theme: Theme<Sector> = { id: key, sector };
    return <BatchCard theme={theme} key={index} count={value} index={index} />;
  });
}

const StyledToggleButton = styled(Expander.Toggle)`
  ${responsive`font-size: ${modularScale({ mobile: -1, desktop: 0 })};`}
  justify-self: center;
  grid-area: expander;
`;

const marginAmount: ResponsiveSizes = modularScale({ mobile: 0.5, desktop: 1 });

const MobileCards = styled.div`
  grid-gap: 0.5rem;
  ${responsive`margin: 0 -${marginAmount} 0 -${marginAmount};`}
  ${responsive`padding: 0 ${marginAmount} 0 ${marginAmount};`}
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  overflow-x: scroll;
  display: grid;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Organise the cards so the first four visible on mobile have the most jobs
 * I could've used the order css property but it'd be kind of painful
 */
// prettier-ignore
function rearrangeForMobile(
  [card1, card2, card3, card4]: readonly React.ReactElement[],
  [card5, card6, card7, card8]: readonly React.ReactElement[]
) {
  return [
    card1, card3, card5, card7,
    card2, card4, card6, card8
  ];
}

export function BatchCards({
  counts,
  sector,
}: BatchCardData): React.ReactElement {
  const cardElements = cards(sector, toList(counts));
  const [firstRow, secondRow] = partition(cardElements.slice(0, 8))(
    (_, i) => i >= 4
  );

  const isMobile: boolean = useMediaQuery({
    query: `(max-width: ${breakpoints["tablet"] - 1}px) and (hover: none)`,
  });

  return (
    <VerticalSpacing>
      <Expander.Context id="themed-batch-cards" initial={true}>
        <CardGrid>
          <Recommended>Recommended for you</Recommended>
          {isMobile ? (
            <MobileCards>{rearrangeForMobile(firstRow, secondRow)}</MobileCards>
          ) : (
            <>
              <FirstRow>
                <CardRow>{firstRow}</CardRow>
              </FirstRow>
              <SecondRow>
                <CardRow>{secondRow}</CardRow>
              </SecondRow>
              <StyledToggleButton />
            </>
          )}
        </CardGrid>
      </Expander.Context>
    </VerticalSpacing>
  );
}
